import { reaction, action, observable, computed, makeObservable } from 'mobx';

import AgentPoll from '../common/agent';

const Agent = AgentPoll.Influenced;

const ITEM_LABEL = 'influenced decision';
const CREATE_SUCCESS = `New ${ITEM_LABEL} added`;
const CREATE_ERROR = `Error creating ${ITEM_LABEL}`;
const REORDER_ERROR = `Error reordering ${ITEM_LABEL}`;
const UPDATE_SUCCESS = 'Saved';
const UPDATE_ERROR = `Error saving ${ITEM_LABEL}`;
const DELETE_SUCCESS = 'Influenced decision removed';
const DELETE_ERROR = `Error deleting ${ITEM_LABEL}`;
const RESOURCE_DELETE_ERROR = 'Error deleting resource';

export default class InfluencedStore {
  _loading = false;

  _initialized = false;

  _error = null;

  _influencedDecisions = [];

  constructor(rootStore, projectStore) {
    makeObservable(this, {
      _influencedDecisions: observable,
      _loading: observable,
      _initialized: observable,
      _error: observable,

      resetFlags: action,

      influenced: computed,
      isLoading: computed,
      isInitialized: computed,
      error: computed,

      loadData: action,
      toggleOpened: action,
    });

    this.rootStore = rootStore;
    this.projectStore = projectStore;
    reaction(
      () => this.projectStore.isInitialized,
      () =>
        this.loadData(
          this.projectStore.project.influencedDecisions,
          this.projectStore.projectDefault.influencedDecisions,
        ),
    );
  }

  get isLoading() {
    return this._loading;
  }

  get isInitialized() {
    return this._initialized;
  }

  get error() {
    return this._error;
  }

  get influenced() {
    return this._influencedDecisions;
  }

  /**
   * Load and prepare data already loaded in ProjectStore
   * @param influencedDecisions
   */
  loadData(influencedDecisions, influencedDecisionsDefault) {
    const tmp = [];
    influencedDecisions.map((item) => {
      const defaultInfluencedDecision = influencedDecisionsDefault.find((infDecDef) => infDecDef._id === item._id);
      tmp.push({
        data: item,
        defaultData: defaultInfluencedDecision,
        formState: { isOpened: false },
      });
      return item;
    });
    this._influencedDecisions = tmp;
    this._initialized = true;
    // console.log(this._influencedDecisions);
  }

  /**
   * Create EMPTY decision  POST /project/:id/influenced-decisions
   * @param projectId
   * @param data
   * @returns {Promise<T>}
   */
  create = (projectId, data) => {
    // console.log('CREATE STORE');
    this._loading = true;
    this._error = null;
    return Agent.create(this.projectStore.project._id)
      .then((res) => {
        // console.log('New member', res.data._id);
        this.rootStore.toastrStore.success(CREATE_SUCCESS);
        this.refreshAndKeepItemOpen(res.data._id);
      })
      .catch((err) => {
        this._error = err;
        this.rootStore.toastrStore.error(CREATE_ERROR, null, err);
      })
      .finally(this.resetFlags);
  };

  async reorder(data) {
    this._loading = false;
    this._error = null;

    try {
      await Agent.reorder(
        this.projectStore.project._id,
        data.map((item) => item.data._id),
      );
      await this.projectStore.refresh();
    } catch (err) {
      this._error = err;
      this.rootStore.toastrStore.error(REORDER_ERROR, null, err);
    } finally {
      this.resetFlags();
    }
  }

  // Update decision
  update(itemId, data) {
    // console.log('UPDATE STORE', data);
    this._loading = true;
    this._error = null;
    return Agent.update(this.projectStore.project._id, itemId, data)
      .then(() => {
        // console.log('New member', res.data._id);
        this.rootStore.toastrStore.success(UPDATE_SUCCESS);
        this.refreshAndKeepItemOpen(itemId);
      })
      .catch((err) => {
        this._error = err;
        this.rootStore.toastrStore.error(UPDATE_ERROR, null, err);
      })
      .finally(this.resetFlags);
  }

  /**
   * Remove decision DELETE /project/:id/influenced-decisions/:decisionId
   * @param itemId
   * @returns {Promise<T>}
   */
  remove = (itemId) => {
    // console.log('DELETE STORE');
    this._loading = true;
    this._error = null;
    return Agent.remove(this.projectStore.project._id, itemId)
      .then(() => {
        this.rootStore.toastrStore.success(DELETE_SUCCESS);
        return this.projectStore.refresh();
      })
      .catch((err) => {
        this._error = err;
        this.rootStore.toastrStore.error(DELETE_ERROR, null, err);
      })
      .finally(this.resetFlags);
  };

  /**
   * Upload decision resource
   * @param decisionId
   * @param file
   * @param progress
   * @returns {Promise<boolean> | Promise<T> | *}
   */
  uploadResource(itemId, file, progress) {
    // console.log('UPLOAD STORE');
    this._loading = true;
    this._error = null;
    return (
      Agent.uploadResource(this.projectStore.project._id, itemId, file, progress)
        // .then(() => this.refreshAndKeepItemOpen(itemId))
        .then(
          action((res) => {
            const item = this._influencedDecisions.find((decision) => decision.data._id === itemId);
            item.data.resource = res.data;
          }),
        )
        .finally(() => (this._loading = false))
    );
  }

  /**
   * remove decision resource
   * @param decisionId
   * @returns {Promise<T>}
   */
  removeResource = (itemId) => {
    // console.log('REMOVE IMAGE STORE');
    this._loading = true;
    this._error = null;
    return (
      Agent.removeResource(this.projectStore.project._id, itemId)
        // .then(() => this.refreshAndKeepItemOpen(itemId))
        .then(
          action(() => {
            const item = this._influencedDecisions.find((decision) => decision.data._id === itemId);
            item.data.resource = null;
          }),
        )
        .catch((err) => {
          this._error = err;
          this.rootStore.toastrStore.error(RESOURCE_DELETE_ERROR, null, err);
        })
        .finally(this.resetFlags)
    );
  };

  /**
   * Toggle item/form open/close status
   * @param itemIds
   */
  toggleOpened = (itemIds) => {
    itemIds.forEach((id) => {
      const influencedDecision = this._influencedDecisions.find((inf) => inf.data._id === id);
      if (influencedDecision) {
        influencedDecision.formState.isOpened = !influencedDecision.formState.isOpened;
      }
    });
  };

  async refreshAndKeepItemOpen(itemId) {
    const openedDecisionsIds = this._influencedDecisions
      .filter((influencedDecision) => influencedDecision.formState.isOpened && influencedDecision.data._id !== itemId)
      .map((inf) => inf.data._id);

    this.projectStore.refresh().then(() => this.toggleOpened([...openedDecisionsIds, itemId]));
  }

  resetFlags = () => {
    this._loading = false;
  };
}
